import { memo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormSelectField from 'components/ui/form/FormSelectField';
import useFormSubmit from 'hooks/useFormSubmit';
import formLocalizedHelperText from 'utils/formLocalizedHelperText';

const validationSchema = yup.object({
  user_id: yup.number().required('settings.staffKpi.validations.userId'),
  type_id: yup.number().required('settings.staffKpi.validations.typeId'),
  count_type: yup.number().required('settings.staffKpi.validations.countType'),
  price: yup
    .number()
    .typeError('settings.staffKpi.validations.price')
    .required('settings.staffKpi.validations.price'),
});

const AddModal = (props) => {
  const { item, setItem, open, setOpen, setRefetch } = props;
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      user_id: '',
      type_id: '',
      count_type: '',
      price: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const method = item?.id ? 'postUpdate' : 'post';
      const message = item?.id
        ? 'settings.staffKpi.alerts.updated'
        : 'settings.staffKpi.alerts.added';
      submit(
        { type: method, contentType: 'simple' },
        values,
        '/admin/staff-kpi',
        { title: t(message) },
        item?.id,
        false,
        handleFinish
      );
    },
  });

  const handleFinish = () => {
    setRefetch(true);
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setItem(null);
    formik.resetForm();
  };

  useEffect(() => {
    if (item?.id) {
      formik.setValues({
        user_id: item.user_id || '',
        type_id: item.type_id || '',
        count_type: item.count_type || '',
        price: item.price || '',
      });
    }
  }, [item]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{ className: '!base-border !shadow-none' }}
      disableEscapeKeyDown
    >
      <DialogTitle className='!bg-gray-100 !mb-2 text-center'>
        <span className='pr-5'>
          {t(`settings.staffKpi.${item?.id ? 'editModal' : 'addModal'}.title`)}
        </span>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit} className='pt-2'>
          <Grid container spacing={{ xs: 2, sm: 2 }} rowSpacing={1} columns={{ xs: 12, sm: 12 }}>
            <Grid item={true} sm={12} xs={12}>
              <FormSelectField
                delay={0}
                fieldName='user_id'
                label={t('common.fields.staff')}
                formik={formik}
                path='/dictionary/staffes'
                isitemsizesmall
              />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormSelectField
                delay={0.1}
                fieldName='type_id'
                label={t('common.fields.type')}
                formik={formik}
                path='/dictionary/kpitypes'
                isitemsizesmall
              />
            </Grid>

            <Grid item={true} sm={6} xs={12}>
              <FormControl
                fullWidth
                error={formik.touched.count_type && Boolean(formik.errors.count_type)}
              >
                <InputLabel>{t('settings.staffKpi.fields.countType')}</InputLabel>
                <Select
                  name='count_type'
                  label={t('settings.staffKpi.fields.countType')}
                  value={formik.values.count_type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value={1}>{t('settings.staffKpi.countType.procent')}</MenuItem>
                  <MenuItem value={2}>{t('settings.staffKpi.countType.sum')}</MenuItem>
                </Select>
                {formik.touched.count_type && formik.errors.count_type && (
                  <FormHelperText>
                    {formLocalizedHelperText(formik.errors.count_type)}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item={true} sm={6} xs={12}>
              <FormCurrencyField
                delay={0.2}
                label={t('settings.staffKpi.fields.price')}
                fieldName='price'
                formik={formik}
              />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0.3}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(AddModal);
